import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Car } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import DocumentCard from './DocumentCard';
import AddDocumentDialog from './AddDocumentDialog';
import { useDemo } from '@/contexts/DemoContext';

const THEME = {
  button: { bg: '#E8E7E5', hoverBg: '#DDDBD8', text: '#4A4745' },
  empty: { bg: '#F8F7F6', border: '#E5E4E2', icon: '#A8A5A1', title: '#4A4745', text: '#6B6966' },
  header: { title: '#3D3B39', subtitle: '#6B6966' },
};

const CarRegistrationTab = ({ searchQuery }) => {
  const { isDemoMode, getDocuments } = useDemo();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => { fetchDocuments(); }, [isDemoMode]);

  const fetchDocuments = async () => {
    try {
      if (isDemoMode) {
        const allDocs = getDocuments() || [];
        const categoryDocs = allDocs.filter(d => d.category_tab === 'car_registration' || d.category === 'car_registration');
        setDocuments(categoryDocs);
        setLoading(false);
        return;
      }
      const response = await API.get('/documents?category=car_registration');
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (isDemoMode) {
      toast.success('Document deleted (demo)');
      fetchDocuments();
      return;
    }
    try {
      await API.delete(`/documents/${id}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const filteredDocs = searchQuery
    ? documents.filter(d => d.title.toLowerCase().includes(searchQuery.toLowerCase()) || (d.issuer || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : documents;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-xl" style={{ backgroundColor: '#F0EFED' }} />
        <Skeleton className="h-32 w-full rounded-xl" style={{ backgroundColor: '#F0EFED' }} />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: THEME.header.title }}>Car Registration</h3>
          <p className="text-sm mt-0.5" style={{ color: THEME.header.subtitle }}>Vehicle titles, registrations, and related documents</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="shrink-0" style={{ backgroundColor: THEME.button.bg, color: THEME.button.text }} data-testid="add-car-reg-btn">
          <Plus className="h-4 w-4 mr-2" />Add Document
        </Button>
      </div>

      {filteredDocs.length === 0 ? (
        <div className="text-center py-12 rounded-xl border-2 border-dashed" style={{ backgroundColor: THEME.empty.bg, borderColor: THEME.empty.border }}>
          <div className="h-14 w-14 mx-auto rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#E8E7E5' }}>
            <Car className="h-7 w-7" style={{ color: THEME.empty.icon }} />
          </div>
          <h4 className="text-lg font-medium mb-2" style={{ color: THEME.empty.title }}>Store Car Documents</h4>
          <p className="mb-4 max-w-sm mx-auto" style={{ color: THEME.empty.text }}>Keep vehicle registrations, titles, and related documents in one place</p>
          <Button onClick={() => setDialogOpen(true)} style={{ backgroundColor: THEME.button.bg, color: THEME.button.text }}>
            <Plus className="h-4 w-4 mr-2" />Add Your First Document
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredDocs.map((doc) => (<DocumentCard key={doc.id} document={doc} onDelete={handleDelete} />))}
        </div>
      )}

      <AddDocumentDialog open={dialogOpen} onOpenChange={setDialogOpen} category="car_registration" onSuccess={() => { fetchDocuments(); setDialogOpen(false); }} />
    </>
  );
};

export default CarRegistrationTab;
